import { useMemo } from "react";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";
import type { AdminProfile } from "@/pages/admin/dashboard/types";
import type { AuditLog } from "@/pages/super-admin/page";

interface AuditTabProps {
  auditLogs: AuditLog[];
  profiles: AdminProfile[];
}

const ACTION_META: Record<string, { icon: string; tone: string }> = {
  create_admin: { icon: "ri-shield-star-line", tone: "text-primary-600" },
  remove_admin: { icon: "ri-user-unfollow-line", tone: "text-accent-600" },
  change_user_role: { icon: "ri-user-settings-line", tone: "text-primary-600" },
  block_user: { icon: "ri-user-forbid-line", tone: "text-accent-600" },
  unblock_user: { icon: "ri-user-received-line", tone: "text-secondary-600" },
  approve_seller: { icon: "ri-checkbox-circle-line", tone: "text-secondary-600" },
  suspend_seller: { icon: "ri-pause-circle-line", tone: "text-accent-600" },
  grant_permission: { icon: "ri-key-2-line", tone: "text-primary-600" },
  revoke_permission: { icon: "ri-lock-2-line", tone: "text-accent-600" },
  transfer_super_admin: { icon: "ri-vip-crown-line", tone: "text-primary-600" },
};

export default function AuditTab({ auditLogs, profiles }: AuditTabProps) {
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    profiles.forEach((p) => map.set(p.id, p.full_name ?? p.email ?? "Usuario"));
    return map;
  }, [profiles]);

  if (auditLogs.length === 0) {
    return (
      <EmptyState
        icon="ri-file-search-line"
        title="Sin registros de auditoría"
        description="Aún no hay acciones administrativas registradas."
      />
    );
  }

  return (
    <div>
      <div className="border-b border-background-200/70 bg-background-100/60 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
        Registro de actividad administrativa (solo lectura)
      </div>
      <ul className="divide-y divide-background-200/60">
        {auditLogs.map((log) => {
          const meta = ACTION_META[log.action] ?? {
            icon: "ri-file-list-3-line",
            tone: "text-foreground-500",
          };
          return (
            <li key={log.id} className="flex items-start gap-3 p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100">
                <i className={`${meta.icon} text-lg ${meta.tone}`}></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-900">
                  {log.action}
                </p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Por{" "}
                  <span className="font-medium text-foreground-700">
                    {log.actor_user_id
                      ? nameById.get(log.actor_user_id) ?? "Desconocido"
                      : "Sistema"}
                  </span>
                  {log.target_user_id && (
                    <>
                      {" "}
                      sobre{" "}
                      <span className="font-medium text-foreground-700">
                        {nameById.get(log.target_user_id) ?? "Desconocido"}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <span className="shrink-0 whitespace-nowrap text-xs text-foreground-400">
                {timeAgo(log.created_at)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}